// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import * as THREE from 'three';

import { api } from '../api/client';
import type { PointCloudScene } from './PointCloudScene';
import { cachedValue, viewerCache } from './viewerCache';

/**
 * Mesh layer of the viewport (#265, review point 2).
 *
 * Loads a PLY geometry blob from the API and adds it to the host
 * {@link PointCloudScene}. Works exactly like `SplatScene`: the host scene and
 * camera are shared, so the mesh is depth-sorted correctly against the point
 * cloud without a second renderer.
 *
 * ## Material
 *
 * The default surface is a lit `MeshStandardMaterial` in a neutral gray taken
 * from the `--mesh-surface` design token. Cell-complex meshes are exported
 * without vertex colours *or* normals, and the earlier `MeshNormalMaterial`
 * rendered them flat black — `normalize((0,0,0))` on a missing normal is `NaN`,
 * which the shader packs as black (#441). `flatShading` sidesteps the missing
 * normals by deriving a per-face normal in the fragment shader, so the facets
 * read under lighting instead of collapsing to a single tone. A mesh that *does*
 * carry usable vertex colours keeps them (`vertexColors`), so this only supplies
 * a colour where the geometry has none. A wireframe toggle switches to
 * `MeshBasicMaterial` so the triangle topology is visible (the cell-complex
 * cells are large triangulated quads; their count is a legible structural hint
 * about room boundaries).
 *
 * ## Lighting
 *
 * The lit surface needs light. The host {@link PointCloudScene} owns the light
 * rig (a hemisphere fill + a directional key, adjustable from the viewport
 * panel, #443); this layer adds none of its own. Points and splats draw with
 * unlit materials, so those lights only ever reach a lit mesh like this one.
 *
 * ## Coordinates
 *
 * PLY coordinates are in the same world frame as the point cloud — both
 * originate from the same `ProjectDB`. The host scene applies a centroid
 * offset to keep float32 precision; the mesh group position is set to the
 * negated origin so the mesh lands on the same grid as the cloud.
 *
 * ## Dynamic import
 *
 * `PLYLoader` is dynamic-imported inside `load()`, as the splat library is in
 * `SplatScene`. The mesh blob is unlikely on most projects, so the loader
 * must not inflate the initial bundle.
 */
/**
 * Fallback surface gray when the `--mesh-surface` token cannot be resolved.
 *
 * A mid neutral gray that reads as lit solid geometry against the near-black
 * viewport canvas. Kept in sync with the token's placeholder value.
 */
const DEFAULT_MESH_GRAY = '#8a8f99';

/** CPU-side bytes of a geometry's attributes and index (the cache's estimate). */
function geometryBytes(geometry: THREE.BufferGeometry): number {
  let bytes = geometry.index?.array.byteLength ?? 0;
  for (const attr of Object.values(geometry.attributes)) bytes += (attr.array as ArrayLike<number> & { byteLength?: number }).byteLength ?? 0;
  return bytes;
}

export class MeshScene {
  private group: THREE.Group | null = null;
  private surfaceMaterial: THREE.MeshStandardMaterial | null = null;
  private wireframeMaterial: THREE.MeshBasicMaterial | null = null;
  private capMaterial: THREE.MeshBasicMaterial | null = null;
  private capMesh: THREE.Mesh | null = null;
  private _caps = false;

  private _visible = true;
  private _wireframe = false;
  private disposed = false;
  private hasColors = false;
  /** The token the surface is drawn in when the geometry has no colour of its own. */
  private colorToken = '--mesh-surface';

  /**
   * @param colorToken The surface colour's design token; default
   *   `--mesh-surface`. An imported model draws each category in its own
   *   (`bygning/modelLayers.ts`), so walls read against the slabs in a plan.
   */
  constructor(
    private readonly host: PointCloudScene,
    colorToken?: string,
  ) {
    if (colorToken) this.colorToken = colorToken;
  }

  /**
   * Download the PLY blob at @p url and add the mesh to the scene.
   *
   * Resolves once the mesh is on screen. Rejects with the loader error,
   * consistent with `SplatScene.load` — a mesh that silently fails to appear
   * is indistinguishable from one that never existed.
   */
  async load(url: string, revision = 0, identity = ''): Promise<void> {
    if (this.disposed) return;

    const { PLYLoader } = await import('three/examples/jsm/loaders/PLYLoader.js');
    if (this.disposed) return;

    // The parsed geometry is kept per case (`viewerCache.ts`, frontend-38f):
    // a remounted viewport reuses it instead of downloading the blob again.
    // Disposing a geometry only frees its GPU buffers, so the next scene
    // simply uploads it again. `revision` (a re-import under the same name)
    // and the server's identity of the mesh (`meshIdentity`: a rewrite by
    // the CLI, with no event) are part of the key.
    const geometry = await cachedValue(
      viewerCache(),
      api.caseId ?? null,
      `mesh\u0000${url}\u0000r${revision}\u0000${identity}`,
      [`mesh:${url}`],
      () =>
        new Promise<THREE.BufferGeometry>((resolve, reject) => {
          new PLYLoader().load(url, resolve, undefined, (error) =>
            reject(error instanceof Error ? error : new Error(String(error))),
          );
        }),
      geometryBytes,
    );

    if (this.disposed) return;

    const posAttr = geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (!posAttr || posAttr.count === 0) return;

    geometry.computeBoundingBox();
    const box = geometry.boundingBox ?? new THREE.Box3().setFromBufferAttribute(posAttr);

    const origin = this.host.registerExternalBounds(box.clone());

    const hasColors = hasUsableVertexColors(geometry);
    this.hasColors = hasColors;
    const surfaceMaterial = new THREE.MeshStandardMaterial({
      // White base lets vertex colours through unmodulated; the neutral gray is
      // only the fallback for geometry that carries no colour of its own.
      color: hasColors
        ? new THREE.Color(0xffffff)
        : this.host.resolveColorToken(this.colorToken, DEFAULT_MESH_GRAY),
      vertexColors: hasColors,
      side: THREE.DoubleSide,
      metalness: 0,
      roughness: 0.85,
      flatShading: true,
    });
    const wireframeMaterial = new THREE.MeshBasicMaterial({
      color: 0x88aacc,
      side: THREE.DoubleSide,
      wireframe: true,
    });

    const mesh = new THREE.Mesh(geometry, this._wireframe ? wireframeMaterial : surfaceMaterial);
    // The measure tools snap to its corners (`snapProbe.ts`); not the caps, which share the geometry.
    mesh.userData.snapTarget = true;

    // Section caps: where a clipping plane cuts a closed solid open, the
    // inside of its far faces shows through the cut. Drawn unlit in the
    // surface colour and pulled forward (so a wall's underside wins over the
    // slab top it stands on), they read as the cut face — the poché of a
    // plan. Only the back faces, only while asked for (`setCaps`).
    // Always the token colour: a mesh with vertex colours has a white
    // surface base, which would make every cut face white.
    const capMaterial = new THREE.MeshBasicMaterial({
      color: this.host.resolveColorToken(this.colorToken, DEFAULT_MESH_GRAY),
      side: THREE.BackSide,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -4,
    });
    const capMesh = new THREE.Mesh(geometry, capMaterial);
    capMesh.visible = this._caps && !this._wireframe;

    const group = new THREE.Group();
    group.position.copy(origin).negate();
    group.visible = this._visible;
    group.add(mesh);
    group.add(capMesh);

    this.host.sceneRoot().add(group);
    this.surfaceMaterial = surfaceMaterial;
    this.wireframeMaterial = wireframeMaterial;
    this.capMaterial = capMaterial;
    this.capMesh = capMesh;
    this.group = group;
  }

  /** Draw the section caps (see {@link load}): on while a section cuts the model. */
  setCaps(caps: boolean): void {
    this._caps = caps;
    if (this.capMesh) this.capMesh.visible = caps && !this._wireframe;
  }

  /**
   * Re-read the surface colour from @p token (a theme switch, or another
   * token). A mesh with its own vertex colours keeps them.
   */
  setColorToken(token: string): void {
    this.colorToken = token;
    const color = this.host.resolveColorToken(token, DEFAULT_MESH_GRAY);
    this.capMaterial?.color.copy(color);
    if (!this.surfaceMaterial || this.hasColors) return;
    this.surfaceMaterial.color.copy(color);
  }

  /** Show or hide the layer without unloading the geometry. */
  setVisible(visible: boolean): void {
    this._visible = visible;
    if (this.group) this.group.visible = visible;
  }

  /** Toggle between normal-vector colouring and wireframe mode. */
  setWireframe(wireframe: boolean): void {
    this._wireframe = wireframe;
    if (!this.group) return;
    if (this.capMesh) this.capMesh.visible = this._caps && !wireframe;
    this.group.traverse((obj) => {
      if (obj instanceof THREE.Mesh && obj !== this.capMesh) {
        obj.material = wireframe
          ? (this.wireframeMaterial ?? obj.material)
          : (this.surfaceMaterial ?? obj.material);
      }
    });
  }

  dispose(): void {
    this.disposed = true;
    const group = this.group;
    this.group = null;
    if (group) {
      group.removeFromParent();
      group.traverse((obj) => {
        if (obj instanceof THREE.Mesh) obj.geometry.dispose();
      });
    }
    this.capMaterial?.dispose();
    this.capMaterial = null;
    this.capMesh = null;
    this.surfaceMaterial?.dispose();
    this.wireframeMaterial?.dispose();
    this.surfaceMaterial = null;
    this.wireframeMaterial = null;
  }
}

/**
 * Whether a geometry carries vertex colours worth showing.
 *
 * Presence of a `color` attribute is not enough: a common export default is an
 * all-black colour attribute, which is exactly the case that makes a mesh
 * invisible. Treat anything darker than a few percent on every channel of every
 * vertex as "no colour", so the neutral-gray fallback applies to it too.
 */
function hasUsableVertexColors(geometry: THREE.BufferGeometry): boolean {
  const color = geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
  if (!color || color.count === 0) return false;

  const data = color.array as ArrayLike<number>;
  // Integer colour attributes (e.g. Uint8 from PLY) are 0..255; float ones are
  // 0..1. ~3% of full scale is the "effectively black" cut in each encoding.
  const threshold = color.normalized || !(color.array instanceof Float32Array) ? 8 : 8 / 255;
  for (let i = 0; i < data.length; i += 1) {
    if (data[i] > threshold) return true;
  }
  return false;
}
